import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  X, Calendar as CalIcon, Sparkles, GraduationCap, Mail,
  CheckCircle2, AlertCircle, ExternalLink, Paperclip, FileText, BookOpen, Clock,
  Brain, MapPin, Trash2, ClipboardList, ChevronDown,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { PdfViewerModal } from "@/components/PdfViewerModal";
import { MaterialPreviewModal } from "@/components/MaterialPreviewModal";
import { QuizModal } from "@/components/QuizModal";
import PracticeExamModal from "@/components/PracticeExamModal";
import { MaterialUploader } from "@/components/MaterialUploader";
import { RelatedSourcesSection } from "@/components/event-detail/RelatedSourcesSection";
import { FootballLiveSection } from "@/components/event-detail/FootballLiveSection";
import { PrepPlanSection } from "@/components/event-detail/PrepPlanSection";
import { formatEventTitle } from "@/lib/utils";

const fmtDateTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("sv-SE", { dateStyle: "long", timeStyle: "short" });
  } catch { return iso; }
};

const CATEGORY_META = {
  test: { label: "Prov", color: "#DC2626", bg: "bg-[#FEE2E2]", fg: "text-[#DC2626]" },
  homework: { label: "Läxa", color: "#E07A5F", bg: "bg-[#FDF2EE]", fg: "text-[#E07A5F]" },
  assignment: { label: "Uppgift", color: "#2A9D8F", bg: "bg-[#E7F4F1]", fg: "text-[#2A9D8F]" },
  event: { label: "Händelse", color: "#3D5A80", bg: "bg-[#EEF2F7]", fg: "text-[#3D5A80]" },
};

const SOURCE_META = {
  classroom_stream: { label: "Classroom", Icon: GraduationCap },
  classroom_assignment: { label: "Classroom-uppgift", Icon: BookOpen },
  gmail: { label: "Gmail", Icon: Mail },
  vklass_detail: { label: "Vklass", Icon: GraduationCap },
  user_upload: { label: "Uppladdat material", Icon: Paperclip },
};

export function EventDetailModal({ eventId, onClose, onChanged, onOpenGmail, onOpenEvent }) {
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [prepPlan, setPrepPlan] = useState(null);
  const [prepLoading, setPrepLoading] = useState(false);
  const [explain, setExplain] = useState(null);
  const [explainLoading, setExplainLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [resolvedLoc, setResolvedLoc] = useState(null);
  const [resolveLoading, setResolveLoading] = useState(false);
  const [preview, setPreview] = useState(null); // {url, title, mime}
  const [materialPreviewId, setMaterialPreviewId] = useState(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [showExam, setShowExam] = useState(false);
  const [examHistory, setExamHistory] = useState([]);
  const [showCatPicker, setShowCatPicker] = useState(false);

  const setCategory = async (cat) => {
    setShowCatPicker(false);
    if (cat === event?.category) return;
    setBusy(true);
    try {
      await api.post(`/events/${eventId}/set-category`, { category: cat });
      toast.success(`Kategori ändrad till ${CATEGORY_META[cat]?.label || cat}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte ändra kategori");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!event?.exam_material || !eventId) { setExamHistory([]); return; }
    let gone = false;
    api.get(`/events/${eventId}/practice-exam/history`)
      .then(({ data }) => { if (!gone) setExamHistory(data.rounds || []); })
      .catch(() => {});
    return () => { gone = true; };
  }, [event?.exam_material, eventId, showExam]);

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onClose]);

  const load = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/events/${eventId}/details`);
      setEvent(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Kunde inte ladda event");
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [eventId]);

  const deleteEvent = async () => {
    if (!window.confirm("Radera händelsen permanent? Den återskapas inte vid nästa synk.")) return;
    setBusy(true);
    try {
      await api.delete(`/events/${eventId}`);
      toast.success("Händelsen raderad");
      onChanged?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte radera");
    } finally {
      setBusy(false);
    }
  };

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${eventId}/accept-ai-suggestion`);
      if (data.ok) {
        toast.success(`Kategori uppdaterad till ${CATEGORY_META[data.category]?.label || data.category}`);
        await load();
        onChanged?.();
      } else {
        toast.info(data.message || "Inget förslag att godkänna");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte uppdatera");
    } finally { setBusy(false); }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await api.post(`/events/${eventId}/reject-ai-suggestion`);
      toast.success("Förslag avvisat");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte avvisa");
    } finally { setBusy(false); }
  };

  const fetchPrepPlan = async () => {
    setPrepLoading(true);
    try {
      const { data } = await api.post(`/events/${eventId}/prep-plan`);
      setPrepPlan(data);
      if (data?.error) toast.info(data.error);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte skapa lässchema");
    } finally { setPrepLoading(false); }
  };

  // Auto-resolve plats via Claude (Värmdö → Stockholm → Sverige) när modal
  // öppnas och eventet har en plats utan cachad resolution. Studiepass och
  // events utan plats hoppas över.
  useEffect(() => {
    if (!event) { setResolvedLoc(null); return; }
    if (event.is_study_session) return;
    const loc = (event.location || "").trim();
    if (!loc) { setResolvedLoc(null); return; }
    // Backend skickar redan med cachad resolution om den finns
    if (event.resolved_location) {
      setResolvedLoc(event.resolved_location);
      return;
    }
    let cancelled = false;
    (async () => {
      setResolveLoading(true);
      try {
        const { data } = await api.post(`/events/${eventId}/resolve-location`);
        if (!cancelled && data?.resolved) setResolvedLoc(data.resolved);
      } catch {
        // Tyst — visa då bara originaltexten
      } finally {
        if (!cancelled) setResolveLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [event?.id, event?.location]);

  // Auto-hämta Edit-förklaring när modal öppnas för läxa/prov MED material
  // (lärartext, beskrivning eller AI-evidence från Classroom/Gmail/PDF).
  // Trigga Edit-förklaring: kör så fort vi har TILLRÄCKLIGT med kontext.
  // Räknas som tillräckligt om något av:
  //   1. lärartexten (vklass_description/description) är >= 20 tecken
  //   2. det finns evidence-items från Classroom/Gmail
  //   3. det finns analyserade `event_materials` (uppladdade dokument / ljud-batch)
  // Backend cachar resultatet och invaliderar automatiskt när nya materials läggs till.
  useEffect(() => {
    if (!event) return;
    if (event.is_study_session) return;
    if (!["homework", "test", "assignment"].includes(event.category)) return;
    const teacherLen = (event.vklass_description || event.description || "").trim().length;
    const hasEvidence = Array.isArray(event.ai_evidence_items) && event.ai_evidence_items.length > 0;
    const hasMaterials = (event.material_count || 0) > 0;
    if (teacherLen < 20 && !hasEvidence && !hasMaterials) return;
    if (explain || explainLoading) return;
    let cancelled = false;
    (async () => {
      setExplainLoading(true);
      try {
        const { data } = await api.post(`/events/${eventId}/explain`);
        if (!cancelled) setExplain(data);
      } catch (e) {
        if (!cancelled) setExplain({ error: "Kunde inte hämta förklaring" });
      } finally {
        if (!cancelled) setExplainLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [event?.id, event?.vklass_description, event?.description, event?.material_count]);

  const currentCat = event?.category || "event";
  const currentMeta = CATEGORY_META[currentCat] || CATEGORY_META.event;
  const aiCat = event?.ai_category;
  const aiMeta = CATEGORY_META[aiCat] || null;
  const conf = event?.ai_confidence;
  const evidence = event?.ai_evidence_items || [];
  const suggestion = event?.ai_suggested_category;
  const autoApplied = event?.ai_auto_applied;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-3 sm:p-6"
      style={{
        paddingTop: "max(0.75rem, env(safe-area-inset-top))",
        paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))",
      }}
      onClick={onClose}
      data-testid="event-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-md sm:max-w-lg rounded-2xl shadow-xl flex flex-col overflow-hidden"
        style={{ maxHeight: "calc(100vh - 48px)" }}
        data-testid="event-modal"
      >
        <header className="px-5 pt-4 pb-3 border-b border-[color:var(--border)] bg-white flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl ${currentMeta.bg} ${currentMeta.fg} flex items-center justify-center flex-shrink-0`}>
            {currentCat === "test" ? <FileText size={18} /> : currentCat === "homework" ? <BookOpen size={18} /> : currentCat === "assignment" ? <ClipboardList size={18} /> : <CalIcon size={18} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="relative">
              {!currentUser?.is_child ? (
                <button
                  onClick={() => setShowCatPicker((v) => !v)}
                  disabled={busy}
                  className={`text-[10px] font-semibold uppercase tracking-wider ${currentMeta.fg} inline-flex items-center gap-0.5`}
                  data-testid="change-category-btn"
                  title="Ändra kategori"
                >
                  {currentMeta.label}
                  <ChevronDown size={11} />
                  {autoApplied && !event?.manual_category && (
                    <span className="ml-1 text-[color:var(--jarvis)]" data-testid="event-ai-auto-badge">· AI ✓</span>
                  )}
                </button>
              ) : (
                <p className={`text-[10px] font-semibold uppercase tracking-wider ${currentMeta.fg}`}>
                  {currentMeta.label}
                  {autoApplied && (
                    <span className="ml-1.5 text-[color:var(--jarvis)]" data-testid="event-ai-auto-badge">· AI ✓</span>
                  )}
                </p>
              )}
              {showCatPicker && (
                <div
                  className="absolute left-0 top-5 z-20 bg-white border border-[color:var(--border)] rounded-xl shadow-lg p-1 flex flex-col min-w-[130px]"
                  data-testid="category-picker"
                >
                  {Object.entries(CATEGORY_META).map(([key, m]) => (
                    <button
                      key={key}
                      onClick={() => setCategory(key)}
                      className={`text-left px-3 py-2 rounded-lg text-xs font-medium hover:bg-[color:var(--bg-elevated)] flex items-center gap-2 ${key === currentCat ? "bg-[color:var(--bg-elevated)]" : ""}`}
                      data-testid={`category-option-${key}`}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: m.color }} />
                      {m.label}
                      {key === currentCat && <CheckCircle2 size={12} className="ml-auto text-[color:var(--moss)]" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <h2 className="text-base font-medium leading-snug">
              {event ? formatEventTitle(event) : (loading ? "Laddar…" : "Event")}
            </h2>
            {event?.start && (
              <p className="text-xs text-[color:var(--text-tertiary)] mt-0.5 flex items-center gap-1">
                <Clock size={11} /> {fmtDateTime(event.start)}
                {event.feed_owner && <span className="ml-1">· {event.feed_owner}</span>}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Synka detta event — kör om Vklass-detail + force-regenererar Edit-explain.
                Bra om läraren just la till information eller du nyss laddade upp material. */}
            {["homework", "test", "assignment"].includes(event?.category) && (
              <button
                onClick={async () => {
                  if (syncing) return;
                  setSyncing(true);
                  try {
                    // 1. Kör Vklass deep-scrape (specifikt för Vklass-events; svarar tyst för andra)
                    if (event?.vklass_id) {
                      await api.post(`/vklass/scrape-assignment-details?limit=5`).catch(() => null);
                    }
                    // 2. Ladda om event-details (kan ha ny vklass_description / material_count)
                    const { data: fresh } = await api.get(`/events/${eventId}/details`);
                    setEvent(fresh);
                    // 3. Force-regenerera Edit-förklaring (dual-brain Claude+OpenAI)
                    setExplainLoading(true);
                    const { data: ex } = await api.post(`/events/${eventId}/explain?force=true`);
                    setExplain(ex);
                    toast.success("Edit uppdaterade förklaringen");
                  } catch (e) {
                    toast.error("Sync misslyckades: " + (e?.response?.data?.detail || e.message));
                  } finally {
                    setSyncing(false);
                    setExplainLoading(false);
                  }
                }}
                disabled={syncing || explainLoading}
                className="rounded-lg p-1.5 hover:bg-[color:var(--bg-elevated)] disabled:opacity-50"
                data-testid="event-modal-sync"
                title="Synka detta event (Edit re-analyserar med dual-brain)"
              >
                <Sparkles size={16} className={syncing ? "animate-pulse text-[color:var(--jarvis)]" : "text-[color:var(--jarvis)]"} />
              </button>
            )}
            {currentUser?.role === "admin" && event && (
              <button
                onClick={deleteEvent}
                disabled={busy}
                className="rounded-lg p-1.5 hover:bg-red-50 text-red-600"
                data-testid="event-modal-delete"
                aria-label="Radera händelsen"
                title="Radera händelsen permanent (återskapas inte vid synk)"
              >
                <Trash2 size={17} />
              </button>
            )}
            <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-[color:var(--bg-elevated)]" data-testid="event-modal-close" aria-label="Stäng">
              <X size={18} />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-auto px-5 py-4 space-y-4 bg-white">
          {loading ? (
            <p className="text-sm text-[color:var(--text-tertiary)] text-center mt-8">Hämtar…</p>
          ) : error ? (
            <p className="text-sm text-[#DC2626]">{error}</p>
          ) : (
            <>
              {/* Studiepass från study_plan — visa fokustext, källor och länk
                  till relaterat prov. Hela modalen är förenklad för studiepass. */}
              {event?.is_study_session && event?.study_session && (
                <section data-testid="event-study-session" className="space-y-3">
                  <div className="rounded-xl p-3 bg-gradient-to-br from-[#F4F8F0] to-[#E8F0DD] border border-[#2C5F2D]/20">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-[#2C5F2D] mb-1.5 flex items-center gap-1.5">
                      <GraduationCap size={11} /> Studiepass · {event.study_session.duration_min} min
                    </p>
                    <p className="text-sm leading-relaxed text-[#1F3F20] font-medium">
                      {event.study_session.focus || "Plugga inför provet"}
                    </p>
                  </div>
                  {Array.isArray(event.study_session.sources) && event.study_session.sources.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-1.5">Källor att använda</p>
                      <ul className="space-y-1">
                        {event.study_session.sources.map((s, i) => (
                          <li key={`src-${i}-${(s || '').slice(0,40)}`} className="text-sm text-[color:var(--text-secondary)] flex items-start gap-2">
                            <span className="text-[#2C5F2D] flex-shrink-0">▸</span>
                            <span>{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {event.related_test && (
                    <button
                      className="w-full text-left rounded-xl border border-[#2C5F2D]/15 px-3 py-2 hover:bg-[#F4F8F0] transition"
                      data-testid="study-session-related-test-btn"
                      onClick={() => {
                        if (typeof onOpenEvent === "function") onOpenEvent(event.related_test.id);
                      }}
                    >
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)]">Prov</p>
                      <p className="text-sm font-medium text-[#1F3F20]">{formatEventTitle(event.related_test)}</p>
                      <p className="text-xs text-[color:var(--text-secondary)]">{event.related_test.start?.slice(0, 10)}</p>
                    </button>
                  )}
                </section>
              )}

              {/* Lärarens uppgift — AI-summa överst om flera källor, sen
                  collapsible per källa om längre än ~140 tecken. Dedup + ren
                  filtrering sker i backend (routes/events.py). */}
              {!event?.is_study_session && Array.isArray(event?.teacher_texts) && event.teacher_texts.length > 0 && (
                <section data-testid="event-teacher-texts">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#2C5F2D] mb-1.5 flex items-center gap-1.5">
                    <GraduationCap size={11} /> Lärarens uppgift
                  </p>
                  <div className="rounded-xl p-3 bg-[#F4F8F0] border border-[#2C5F2D]/15 space-y-2.5">
                    {event.teacher_summary && (
                      <p
                        className="text-sm leading-relaxed font-medium text-[#1F3F20] pb-2 border-b border-[#2C5F2D]/15"
                        data-testid="event-teacher-summary"
                      >
                        {event.teacher_summary}
                      </p>
                    )}
                    {event.teacher_texts.map((t, idx) => {
                      const isLong = (t.text || "").length > 140;
                      const sourceLabel = (
                        t.source === "vklass" ? "Vklass" :
                        t.source === "gmail" ? "E-post" : "Classroom"
                      );
                      const headerText = t.title || (t.text || "").split("\n")[0].slice(0, 80);
                      if (isLong || event.teacher_texts.length > 1) {
                        return (
                          <details
                            key={`teacher-d-${idx}-${(t.title || t.text || '').slice(0,40)}`}
                            className="group"
                            data-testid={`event-teacher-source-${idx}`}
                            {...(idx === 0 && !event.teacher_summary ? { open: true } : {})}
                          >
                            <summary className="cursor-pointer list-none flex items-center gap-2 text-xs text-[color:var(--text-secondary)] hover:text-[#2C5F2D]">
                              <span className="transition-transform group-open:rotate-90 text-[10px]">▸</span>
                              <span className="font-medium text-[#2C5F2D]/80 text-[10px] uppercase tracking-wide">{sourceLabel}</span>
                              <span className="truncate flex-1 text-left">{headerText}</span>
                            </summary>
                            <p
                              className="text-sm whitespace-pre-line leading-relaxed pl-5 pt-1.5 text-[color:var(--text-primary)]"
                              data-testid={`event-teacher-text-${idx}`}
                            >
                              {t.text}
                            </p>
                          </details>
                        );
                      }
                      return (
                        <p
                          key={`teacher-p-${idx}-${(t.text || '').slice(0,40)}`}
                          className="text-sm whitespace-pre-line leading-relaxed"
                          data-testid={`event-teacher-text-${idx}`}
                        >
                          {t.text}
                        </p>
                      );
                    })}
                  </div>
                </section>
              )}
              {/*
                Beskrivning: prioritera `vklass_description` (lärartext) över `description`
                (iCal-feeden från Vklass innehåller bara datum/tid som "Onsdag 20 maj kl 10:00",
                medan `vklass_description` har det riktiga uppgiftsinnehållet).

                Faller bara tillbaka till `description` om det INTE ser ut som ett
                rent datum-utdrag.
              */}
              {(() => {
                const vdesc = (event?.vklass_description || "").trim();
                const desc = (event?.description || "").trim();
                const looksLikeDate = /^[A-ZÅÄÖa-zåäö]+\s+\d+\s+\w+\s+\d{4}\s+kl/i.test(desc);
                const best = vdesc || (looksLikeDate ? "" : desc);
                const hasTeachers = Array.isArray(event?.teacher_texts) && event.teacher_texts.length > 0;
                return best && !hasTeachers ? (
                  <section data-testid="event-description">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-1.5">
                      Beskrivning
                    </p>
                    <p className="text-sm whitespace-pre-line leading-relaxed">{best}</p>
                    {vdesc && desc && desc !== vdesc && !looksLikeDate && (
                      <details className="text-xs text-[color:var(--text-tertiary)] mt-2" data-testid="event-description-ical-fallback">
                        <summary className="cursor-pointer hover:text-[color:var(--text-secondary)]">
                          Visa även iCal-beskrivning
                        </summary>
                        <p className="mt-1.5 whitespace-pre-line">{desc}</p>
                      </details>
                    )}
                  </section>
                ) : null;
              })()}
              {(() => {
                const hasTeachers = Array.isArray(event?.teacher_texts) && event.teacher_texts.length > 0;
                if (!hasTeachers) return null;
                const vdesc = (event?.vklass_description || "").trim();
                const desc = (event?.description || "").trim();
                const looksLikeDate = /^[A-ZÅÄÖa-zåäö]+\s+\d+\s+\w+\s+\d{4}\s+kl/i.test(desc);
                const fallback = vdesc || (looksLikeDate ? "" : desc);
                return fallback ? (
                  <details className="text-xs text-[color:var(--text-tertiary)]" data-testid="event-description-fallback">
                    <summary className="cursor-pointer hover:text-[color:var(--text-secondary)]">
                      Visa {vdesc ? "Vklass-beskrivning" : "iCal-beskrivning"}
                    </summary>
                    <p className="mt-1.5 whitespace-pre-line">{fallback}</p>
                  </details>
                ) : null;
              })()}
              {event?.location && (
                <section data-testid="event-location">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-1 flex items-center gap-1.5">
                    <MapPin size={11} /> Plats
                  </p>
                  {resolvedLoc ? (
                    <a
                      href={resolvedLoc.maps_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block rounded-xl p-2.5 bg-[#F4F8F0] border border-[#2C5F2D]/15 hover:bg-[#E8F0DD] transition active:scale-[0.98]"
                      data-testid="event-location-link"
                    >
                      <p className="text-sm font-medium text-[#1F3F20] flex items-center gap-1.5">
                        {event.location}
                        <ExternalLink size={11} className="opacity-60" />
                      </p>
                      <p className="text-[11px] text-[color:var(--text-secondary)] mt-0.5 leading-snug">
                        {resolvedLoc.address}
                      </p>
                      <p className="text-[9px] uppercase tracking-wider text-[#2C5F2D]/60 mt-1">
                        Öppna i kartan
                        {resolvedLoc.scope && resolvedLoc.scope !== "okand" && (
                          <span> · {resolvedLoc.scope === "varmdo" ? "Värmdö" : resolvedLoc.scope === "stockholm" ? "Stockholm" : "Sverige"}</span>
                        )}
                      </p>
                    </a>
                  ) : (
                    <p className="text-sm flex items-center gap-1.5">
                      {event.location}
                      {resolveLoading && <span className="text-[10px] text-[color:var(--text-tertiary)] italic">söker adress…</span>}
                    </p>
                  )}
                </section>
              )}

              {/* AI-innehåll (Innehåll / Att göra / Steg / Tips) — primärt fokus */}
              {(explainLoading || (explain && !explain.error && !explain.needs_text)) && (
                <section
                  className="rounded-2xl p-4 bg-white border border-[color:var(--border)]"
                  data-testid="event-explain-summary"
                >
                  {explainLoading && (
                    <p className="text-sm text-[color:var(--text-tertiary)] italic flex items-center gap-1.5">
                      <Brain size={14} className="text-[color:var(--jarvis)] animate-pulse" />
                      Edit läser igenom material...
                    </p>
                  )}
                  {!explainLoading && explain && !explain.error && !explain.needs_text && (
                    <>
                      {explain.summary && (
                        <div className="mb-3">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--jarvis)] mb-1">
                            Innehåll
                          </p>
                          <p className="text-sm leading-relaxed text-[color:var(--text-primary)]">
                            {explain.summary}
                          </p>
                        </div>
                      )}
                      {explain.what_to_do && (
                        <div className="mb-3">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--jarvis)] mb-1">
                            Att göra
                          </p>
                          <p className="text-sm leading-relaxed text-[color:var(--text-secondary)]">
                            {explain.what_to_do}
                          </p>
                        </div>
                      )}
                      {Array.isArray(explain.steps) && explain.steps.length > 0 && (
                        <ol className="list-decimal list-inside text-xs text-[color:var(--text-secondary)] mt-2 space-y-0.5">
                          {explain.steps.map((s, i) => (
                            <li key={`step-${i}-${(s || '').slice(0,40)}`}>{s}</li>
                          ))}
                        </ol>
                      )}
                      {explain.tips && (
                        <p className="text-xs font-semibold text-[color:var(--text-primary)] mt-2">
                          💡 {explain.tips}
                        </p>
                      )}
                      {explain.time_estimate_min > 0 && (
                        <p className="text-[11px] font-semibold text-[color:var(--text-primary)] mt-1.5 flex items-center gap-1">
                          <Clock size={10} /> Uppskattad tid: ~{explain.time_estimate_min} min
                        </p>
                      )}
                      {explain.material_summary && (
                        <div
                          className="mt-4 pt-3 border-t border-[color:var(--border-subtle)]"
                          data-testid="event-explain-material-summary"
                        >
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-1.5 flex items-center gap-1">
                            <BookOpen size={10} /> Sammanfattning av material
                          </p>
                          <p className="text-xs leading-relaxed text-[color:var(--text-primary)] whitespace-pre-line">
                            {explain.material_summary}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </section>
              )}

              {/* Edit-kategori (diskret rad — kategori + säkerhet i ljusgrått) */}
              {event?.ai_reviewed_at && (
                <div
                  className="flex items-center gap-1.5 text-[10px] text-[color:var(--text-tertiary)] uppercase tracking-wider px-1"
                  data-testid="event-ai-meta"
                  title={event.ai_reasoning || ""}
                >
                  <Sparkles size={10} />
                  <span>
                    Edit: {currentCat === "test" ? "Prov" : currentCat === "homework" ? "Läxa" : "Händelse"}
                  </span>
                  {typeof conf === "number" && (
                    <span className="ml-auto">{Math.round(conf * 100)}% säker</span>
                  )}
                </div>
              )}

              {/* Edit-förslag (kategori-omkategorisering) */}
              {event?.ai_reviewed_at && suggestion && suggestion !== currentCat && (
                <div
                  className="rounded-xl p-3 bg-[color:var(--bg-elevated)] border border-[color:var(--border)]"
                  data-testid="event-ai-suggestion"
                >
                  <p className="text-xs flex items-center gap-1.5 mb-2">
                    <AlertCircle size={12} className="text-[color:var(--jarvis)]" />
                    Edit föreslår: <strong>{CATEGORY_META[suggestion]?.label || suggestion}</strong>
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={accept}
                      disabled={busy}
                      className="text-xs font-medium px-3 py-1.5 rounded-full bg-[color:var(--moss)] text-white disabled:opacity-50"
                      data-testid="event-ai-accept"
                    >
                      <CheckCircle2 size={11} className="inline mr-1" /> Godkänn
                    </button>
                    <button
                      onClick={reject}
                      disabled={busy}
                      className="text-xs font-medium px-3 py-1.5 rounded-full border border-[color:var(--border)] disabled:opacity-50"
                      data-testid="event-ai-reject"
                    >
                      Avvisa
                    </button>
                  </div>
                </div>
              )}

              {/* Min Fotboll live-resultat (om eventet är bevakat) */}
              <FootballLiveSection eventId={event?.id} />

              {/* Relaterade källor */}
              {evidence.length > 0 && (
                <RelatedSourcesSection
                  evidence={evidence}
                  SOURCE_META={SOURCE_META}
                  onOpenGmail={onOpenGmail}
                  setPreview={setPreview}
                  onOpenMaterial={setMaterialPreviewId}
                />
              )}

              {/* Prov-prep lässchema (endast för prov) */}
              {["homework", "test"].includes(currentCat) && (
                <MaterialUploader
                  eventId={event.id}
                  eventCategory={currentCat}
                  onAnalysisReady={() => {
                    // När nytt material analyserats — invalidera prep-plan så användaren kan generera om
                    setPrepPlan(null);
                  }}
                />
              )}

              {/* Lässchema — visas för prov om vi har KÄLLA att basera planen på
                  (lärartext, evidence-items från Classroom/Gmail, ELLER uppladdat material). */}
              {currentCat === "test" && (evidence.length > 0 || (event?.material_count || 0) > 0) && (
                <PrepPlanSection
                  prepPlan={prepPlan}
                  prepLoading={prepLoading}
                  fetchPrepPlan={fetchPrepPlan}
                  SOURCE_META={SOURCE_META}
                  onOpenGmail={onOpenGmail}
                />
              )}

              {/* Övningsprov & Edit-quiz (läxor OCH prov med källa) */}
              {["test", "homework"].includes(currentCat) && (event?.exam_material || evidence.length > 0 || (event?.material_count || 0) > 0) && (
                <section data-testid="event-quiz-section" className="space-y-2">
                  {event?.exam_material && (
                    <>
                      <button
                        onClick={() => setShowExam(true)}
                        className="w-full flex items-center justify-center gap-2 text-sm font-medium px-4 py-3 rounded-xl bg-[color:var(--jarvis)] text-white hover:opacity-90 transition-colors"
                        data-testid="event-practice-exam-btn"
                      >
                        <FileText size={16} />
                        Skapa övningsprov
                      </button>
                      <p className="text-[11px] text-[color:var(--text-tertiary)] text-center">
                        15-20 frågor ENDAST ur uppladdat/länkat material — varje fråga beläggs med citat.
                      </p>
                      {examHistory.length > 0 && (
                        <p className="text-[11px] text-center text-[color:var(--text-secondary)]" data-testid="event-exam-history">
                          Tidigare prov: {examHistory.map((h) => `${h.correct}/${h.total}`).join(" → ")}
                          {examHistory.length > 1 && examHistory[examHistory.length - 1].correct > examHistory[0].correct ? " 📈" : ""}
                        </p>
                      )}
                    </>
                  )}
                  {(evidence.length > 0 || (event?.material_count || 0) > 0) && (
                    <>
                      <button
                        onClick={() => setShowQuiz(true)}
                        className="w-full flex items-center justify-center gap-2 text-sm font-medium px-4 py-3 rounded-xl border border-[color:var(--jarvis)] text-[color:var(--jarvis)] hover:bg-[color:var(--jarvis)] hover:text-white transition-colors"
                        data-testid="event-quiz-btn"
                      >
                        <Sparkles size={16} />
                        Quiz med Edit (material + webb)
                      </button>
                      <p className="text-[11px] text-[color:var(--text-tertiary)] text-center">
                        10 frågor baserade på materialet, kompletterat med webbkällor.
                      </p>
                    </>
                  )}
                </section>
              )}

              {!event?.ai_reviewed_at && (
                <p className="text-xs text-[color:var(--text-tertiary)] italic" data-testid="event-no-ai">
                  Edit har inte granskat detta event än. Auto-granskning körs efter nästa sync.
                </p>
              )}
            </>
          )}
        </div>
      </div>
      {preview && (
        <PdfViewerModal
          url={preview.url}
          title={preview.title}
          mime={preview.mime}
          pages={preview.pages}
          startIndex={preview.startIndex || 0}
          onClose={() => setPreview(null)}
        />
      )}
      {materialPreviewId && (
        <MaterialPreviewModal
          materialId={materialPreviewId}
          open={!!materialPreviewId}
          onClose={() => setMaterialPreviewId(null)}
        />
      )}
      {showQuiz && (
        <QuizModal
          eventId={eventId}
          eventTitle={formatEventTitle(event)}
          onClose={() => setShowQuiz(false)}
        />
      )}
      {showExam && (
        <PracticeExamModal
          eventId={eventId}
          eventTitle={formatEventTitle(event)}
          onClose={() => setShowExam(false)}
        />
      )}
    </div>
  );
}

export default EventDetailModal;
